import { NO_CATEGORY_DATA } from "./unknownBucket";
import saveAs from "file-saver";
import { IAdmins } from "../Interfaces/AdminInterface";
import config from "../config/config";
import { IUsers } from "../Interfaces/UserInterface";
import {
  adminExportCsvOption,
  userExportCsvOption,
} from "../constants/commonConstants";
import { toastMessageError } from "../components/utilities/commonToastMessage";
import storage from "./storage";

export const trakingCodeGenrator = (trackingId: string) => {
  /*
   * ONE script tag (2026-08-27). This used to be three: socket.io from cdnjs,
   * env.js, and the loader.
   *
   * - socket.io is now bundled into analytics.js, so customer pages no longer
   *   depend on a third-party CDN being up for tracking to work.
   * - env.js only ever carried the host analytics.js was served from, which
   *   analytics.js now derives from its own script URL.
   *
   * Both changes are backwards compatible: analytics.js still prefers
   * window.__env when present, and the bundled socket client shadows any
   * global `io` a three-tag install also loads. Existing installs keep working
   * untouched - which matters, because this file cannot be versioned or
   * recalled.
   */
  return `<!-- Begin Skydive Tracking Code -->
<script>
    (function(i, s, o, g, r, a, m) {
      i['skydiveObject'] = {name: r, scriptUrl: g};
      a = s.createElement(o),
      m = s.getElementsByTagName(o)[0];
      a.async = 1;
      a.src = g;
      m.parentNode.insertBefore(a, m);
      a.onload = function() {
          skydive.init('${trackingId}');
      }
    })(window, document, 'script', '${config.socketPath}analytics.js', 'skydive');
</script>
<!-- End Skydive Tracking Code -->`;
};


/*
 * Masks an email for display on a screen the user has already reached.
 *
 * 🔴 ADDED 2026-09-04. The 2FA screens rendered the FULL address -
 * "We just sent an email to chris@example.com" - in both AdminTwoFactor and
 * Signup.
 *
 * The person reading it already knows their own address, so this is not about
 * secrecy from them. It is about everyone ELSE who can see the screen: someone
 * over a shoulder in a cafe, a screen share, a support call, a screenshot
 * pasted into a ticket. Showing enough to confirm WHICH address without
 * publishing the whole thing is why every bank does it this way.
 *
 * Keeps the first and last character of the local part and the full domain -
 * "c•••••n@example.com" - which is enough to answer "is that the right
 * inbox?" and not enough to hand the address to a passer-by.
 *
 * Degrades safely: anything that is not a recognisable email is returned
 * unchanged rather than throwing, because this runs during a login flow where
 * an exception would be worse than an unmasked string.
 */
export const maskEmail = (email: string): string => {
  if (typeof email !== "string") {
    return "";
  }

  const at = email.lastIndexOf("@");
  if (at < 1) {
    return email;
  }

  const local = email.slice(0, at);
  const domain = email.slice(at);

  // Too short to mask meaningfully - one character revealed either side would
  // BE the whole local part.
  if (local.length <= 2) {
    return `${local[0]}${"•".repeat(Math.max(local.length - 1, 1))}${domain}`;
  }

  return `${local[0]}${"•".repeat(local.length - 2)}${local[local.length - 1]}${domain}`;
};

export const averageSessionDurationConverter = (time: number) => {
  /*
   * 🔴 GUARDED 2026-08-27. Rendered literal "NaN:NaN:NaN" in the metrics bar
   * whenever `time` was undefined or null - seen live on staging the moment
   * playback refused a day, because a restricted response returns
   * `summary: null` and every field read off it is undefined.
   *
   * Not only a restricted day: any account with no completed sessions yet
   * hits the same path, which means a BRAND NEW customer's very first look at
   * their own dashboard could show NaN. Zero is the honest answer - there is
   * genuinely no average to report.
   */
  if (time == null || Number.isNaN(Number(time))) {
    return "00:00:00";
  }

  let seconds = Number(time) / 1000;
  // 2- Extract hours:
  const hours = parseInt(String(seconds / 3600)); // 3,600 seconds in 1 hour
  seconds = seconds % 3600; // seconds remaining after extracting hours
  // 3- Extract minutes:
  const minutes = parseInt(String(seconds / 60)); // 60 seconds in 1 minute
  // 4- Keep only seconds not extracted to minutes:
  seconds = seconds % 60;
  return `${hours < 10 ? `0${hours.toFixed()}` : hours.toFixed()}:${
    minutes < 10 ? `0${minutes.toFixed()}` : Math.trunc(minutes).toFixed()
  }:${seconds < 10 ? `0${seconds.toFixed()}` : Math.trunc(seconds).toFixed()}`;
};

export const exportDataToCSV = async (
  data: IAdmins[] | IUsers[],
  headers: string[],
  fileName: string
) => {
  const csvData: string | undefined = convertToCSV(data, headers, fileName);
  const blob = new Blob([csvData as string], {
    type: "text/csv;charset=utf-8",
  });
  saveAs(blob, fileName);
};

const convertToCSV = (
  data: IAdmins[] | IUsers[],
  headers: string[],
  fileName: string
) => {
  if (fileName === adminExportCsvOption.FILE_NAME) {
    const rows = (data as IAdmins[]).map((item: IAdmins) => {
      const permissions = item.permissions
        ? capitalizeFirstLetter(item.permissions)
        : "" || "";
      return [
        item.firstname ? capitalizeFirstLetter(item.firstname) : "" || "",
        item?.email || "",
        item.user_role ? capitalizeFirstLetter(item.user_role) : "" || "",
        item.status ? capitalizeFirstLetter(item.status) : "" || "",
        permissions || "",
      ];
    });

    const csvContent =
      headers.join(",") + "\n" + rows.map((row) => row.join(",")).join("\n");

    return csvContent;
  } else if (fileName === userExportCsvOption.FILE_NAME) {
    const rows = (data as IUsers[]).map((item: IUsers) => {
      const siteUrl = item?.sites ? item.sites[0]?.siteUrl : "-";
      const createdAt =
        item?.createdAt && new Date(item?.createdAt).toLocaleDateString();
     
      return [
        item?.firstname || "",
        item?.email || "",
        siteUrl || "",
        createdAt || "",
        "days(days)",
      ];
    });

    const csvContent =
      headers.join(",") + "\n" + rows?.map((row) => row.join(",")).join("\n");

    return csvContent;
  } else {
    toastMessageError("Something went wrong!");
  }
};

export const capitalizeFirstLetter = (input: string | string[]) => {
  if (Array.isArray(input)) {
    return input
      .map((str) => str.charAt(0).toUpperCase() + str.slice(1))
      .join(" & ");
  } else if (typeof input === "string") {
    return input.charAt(0).toUpperCase() + input.slice(1);
  } else {
    return input;
  }
};

export const shortenString = (s:any) => {
  if (s.length > 1) {
      return s.slice(0, 1) + "...";
  } else {
      return s;
  }
};


/*
 * Everything that must NOT survive into the next account's session.
 *
 * 🔴 EXPANDED 2026-09-03. This cleared five keys while the app wrote
 * seventeen, and the gap included plan ENTITLEMENTS.
 *
 * `mainViewSlots` was already here - so the rule "an entitlement must not
 * outlive the logout" was understood - but `urlsToTrack` and
 * `interactionPulsesEnabled` are exactly the same kind of value and were
 * missed. That is not academic:
 *
 *   * pagesModel reads storage.get("urlsToTrack") to decide whether a new page
 *     may be tracked at all, and
 *   * clusterService reads interactionPulsesEnabled AT MODULE LOAD, so it is
 *     baked in from whatever the previous session left behind.
 *
 * On a shared browser that means the next person to log in inherits the
 * previous account's limits - a Base Jump user tracking unlimited URLs, or a
 * paying customer silently capped at the free tier's.
 *
 * Identity is cleared for the same reason: `email`, the login payloads, and
 * `viewedAsAdminUserId` - an admin impersonation marker outliving a logout is
 * its own problem, and it was previously removed in ONE AnalyticsView code
 * path rather than on logout.
 *
 * Deliberately NOT cleared: `bubbleParameters`, `useIcons`, `pageViewDisabled`
 * and the three *Dismissed nag flags. Those are genuine per-browser display
 * preferences, not entitlements or identity, and wiping them would make the UI
 * forget itself on every logout for no safety benefit.
 *
 * `storage.remove` rather than `set(key, "")` - an empty string is a VALUE, and
 * `Number(storage.get("urlsToTrack"))` reads "" as 0, which is a real
 * entitlement (track nothing) rather than "unset". The five original keys keep
 * their historical set("") behaviour only where a caller may already depend on
 * the empty string; the entitlement keys must be genuinely absent.
 */
/*
 * THE CACHED PAGE LIST IS KEYED BY SITE (2026-09-28).
 *
 * 🔴 IT WAS ONE GLOBAL KEY, `pages`, with THIRTEEN call sites. Safe for
 * exactly as long as an account could own one site. After multi-site shipped,
 * the key held whichever site wrote last, and readers handed it to setPages()
 * with a DIFFERENT site's id - so the dashboard rendered one site's page list
 * under another site's name.
 *
 * Chris hit it as three symptoms that were all this: /about in two slot
 * boxes, selection refusing to advance to the next free slot, and pages that
 * looked deleted. findEmptyIndex was computing slots from the wrong site's
 * pages, so the model and the server disagreed about what existed.
 *
 * 🔴 CLEARING ON SWITCH WAS NOT ENOUGH, which is why this replaced it. That
 * only covered the dropdown path and left the key global - any write for site
 * A while site B was open re-corrupted it. Scoping makes the two physically
 * unable to collide.
 *
 * 🔴 THE KEY IS DERIVED HERE AND NOWHERE ELSE. Thirteen call sites each
 * building their own `"pages:" + siteId` is thirteen chances to forget one,
 * and a single missed caller keeps the bug alive while looking fixed.
 *
 * A missing siteId returns null rather than falling back to a shared key:
 * "no site" must read as "no cache", never as "everyone's cache".
 */
const pagesKeyFor = (siteId?: string | null): string | null => {
  const id = siteId ?? storage.get("siteId");

  return id ? `pages:${id}` : null;
};

/*
 * 🔴 THE CACHE STAMPS THE SITE IT WAS WRITTEN FOR, AND READS VERIFY IT.
 *
 * Scoping the KEY was not enough. Writers pass the CURRENT siteId alongside
 * whatever pages they happen to hold, so a caller carrying the previous
 * site's list writes it under the new site's key - and every guard that
 * compares ids waves it through, because the id is right and only the
 * CONTENTS are wrong.
 *
 * Measured: second-site.test has four pages (/, home, and two probe pages)
 * and does NOT have /categories, yet the dashboard listed staging's nineteen.
 * REMOVE then failed because the server correctly refused to unselect a page
 * that site does not have - the visible bug was a symptom of the wrong list.
 *
 * The stamp is written INSIDE the payload, so a poisoned entry identifies
 * itself on read and is discarded. Self-healing: no migration, and a browser
 * already carrying bad data recovers on its next read.
 *
 * Legacy shape (a bare array, from before the stamp) is accepted ONLY when it
 * is read under the key it was written for - it cannot be attributed to
 * another site, which is the whole failure mode.
 */
type CachedPages = { siteId: string; pages: any[] };

export const getCachedPages = (siteId?: string | null): any[] | null => {
  const key = pagesKeyFor(siteId);

  if (!key) {
    return null;
  }

  const raw = storage.getJSON<CachedPages | any[]>(key);

  if (!raw) {
    return null;
  }

  /*
   * 🔴 AN UNSTAMPED ENTRY IS DISCARDED, NOT TRUSTED.
   *
   * A bare array predates the stamp, which means it CANNOT be verified - and
   * the entries already sitting in browsers right now are exactly the
   * poisoned ones (second-site.test's key holding staging's nineteen pages).
   * Accepting them "for compatibility" would leave the live bug intact for
   * every existing session while looking fixed.
   *
   * The cost of discarding is one refetch from the server, which is
   * authoritative anyway.
   */
  if (Array.isArray(raw)) {
    storage.remove(key);

    return null;
  }

  const expected = String(siteId ?? storage.get("siteId") ?? "");

  if (!raw.siteId || String(raw.siteId) !== expected) {
    // eslint-disable-next-line no-console
    console.warn(
      `[skydive] cached pages discarded: written for site ${raw.siteId}, read for ${expected}.`
    );
    storage.remove(key);

    return null;
  }

  return raw.pages ?? null;
};

export const setCachedPages = (pages: any[], siteId?: string | null): void => {
  const key = pagesKeyFor(siteId);
  const id = siteId ?? storage.get("siteId");

  if (key && id) {
    storage.set(key, JSON.stringify({ siteId: String(id), pages }));
  }
};

export const clearCachedPages = (siteId?: string | null): void => {
  const key = pagesKeyFor(siteId);

  if (key) {
    storage.remove(key);
  }
};

/*
 * PER-SITE CACHES, CLEARED WHEN THE SITE CHANGES (2026-09-28).
 *
 * 🔴 THE BUG THIS FIXES: `pages` is cached in localStorage under ONE GLOBAL
 * KEY with no site scoping. That was safe for exactly as long as an account
 * could own one site. After a switch, RightMenu/clusterService read the key
 * and got the PREVIOUS site's pages, then handed them to setPages() with the
 * NEW site's id - so the dashboard showed one site's page list under another
 * site's name.
 *
 * Chris hit it as: /about appearing in two slot boxes, selections refusing to
 * advance to the next free slot, and pages that looked deleted. All three are
 * the same cause - findEmptyIndex was computing slots from the wrong site's
 * page set, so the model and the server disagreed about what existed.
 *
 * 🔴 CLEARED, NOT MIGRATED. Scoping the key per site would mean touching nine
 * call sites in one pass, and a single missed reader keeps the bug alive
 * while looking fixed. Clearing is one place, and the failure mode if a read
 * beats the refetch is an EMPTY list that fills in - not a confidently wrong
 * one. Empty-then-correct beats wrong-and-stable.
 *
 * Deliberately NOT clearing display preferences (bubbleParameters and the
 * like): those are the customer's choices about how they like to look at
 * things, not facts about a site.
 */
export const clearPerSiteCaches = () => {
  // The page cache is keyed per site now, so this clears the CURRENT site's
  // entry. Kept because the other two are still global, and because clearing
  // on switch remains correct belt-and-braces alongside the scoping.
  clearCachedPages();
  storage.set("totalSession", "");
  storage.set("socketId", "");
};

export const emptyLocalStorageOnLogout = ()=>{
  storage.set("socketId","");
  storage.set("mainViewSlots","");
  // The CURRENT site's scoped cache. Must run BEFORE siteId is cleared -
  // the key is derived from it.
  clearCachedPages();
  /*
   * The legacy GLOBAL key, kept deliberately (2026-09-28).
   *
   * Page caches are keyed per site now, but a browser that was logged in
   * before that change still holds the old unscoped `pages` entry. Nothing
   * reads it any more, so it is inert - but leaving stale page data for an
   * account someone has logged out of is not something to leave lying around.
   * This clears it once, on the next logout, and then it is gone for good.
   */
  storage.set("pages","");
  storage.set("siteId","");
  storage.set("totalSession","");

  // Entitlements - must never outlive the account they belong to.
  storage.remove("urlsToTrack");
  storage.remove("interactionPulsesEnabled");

  // The Historical range is a preference of whoever was logged in.
  storage.remove("historicalRange");
  storage.remove("flowLinesState");
  storage.remove("flowLinesShown");
  storage.remove("happyPathEnabled");
  storage.remove("hideUnknownBuckets");
  storage.remove("historicalLayout");
  storage.remove("historicalRowsOrder");
  storage.remove("historicalLabels");
  storage.remove("gooeyEffectEnabled");
  storage.remove("dimLeavingEnabled");
  storage.remove("leaveStyle");
  storage.remove("slotTotalsEnabled");
  storage.remove("slotSortOrder");

  // Identity and session.
  storage.remove("email");
  storage.remove("loginData");
  storage.remove("loginAdminData");
  storage.remove("viewedAsAdminUserId");
};

// Visitor Type (bot/human/AI - menuType "visitor_category") values are raw
// enum strings straight from Common/Helper/visitorClassification.ts
// ('search_engine_bot', 'other_bot', 'ai_bot', 'human'), never meant to be
// displayed as-is - underscores and no capitalization. Deliberately scoped
// to a single caller-chosen value rather than applied to every category
// label everywhere: several OTHER filters' values are already correctly,
// deliberately cased on their own (e.g. "iOS", "macOS", "United States")
// and a blanket capitalize-first-letter would mangle those (e.g. "iOS" ->
// "IOS").
// Explicit labels for the visitor_category values, because the generic
// underscore formatter below renders "ai_agent" as "Ai Agent" - which looks
// careless on exactly the screen you would show a prospect. Keyed on the exact
// enum strings, so no other filter's values can be caught by accident (several
// are already correctly cased on their own - "iOS", "macOS", "United States").
//
// Wording matches the agreed taxonomy: "bot" is the wrong word for anything
// with a person behind it, and "crawler" is more precise and less pejorative
// for the machines. See Common/Helper/visitorClassification.ts.
const VISITOR_CATEGORY_LABELS: Record<string, string> = {
  human: "Human",
  ai_agent: "AI Agent",
  ai_crawler: "AI Crawler",
  // Legacy: recorded before the agent/crawler split on 2026-08-24. Labelled so
  // historical data reads honestly rather than implying a precision it does
  // not have.
  ai_bot: "AI Bot (legacy)",
  search_engine_bot: "Search Crawler",
  other_bot: "Other Bot",
  unidentified: "Unidentified",
};

// Definitions for the Visitor Type filter, shown on hover.
//
// Written FROM WHAT THE CODE ACTUALLY KNOWS, not from textbook analytics -
// same rule the Footer's eight metric definitions follow. A tooltip describing
// something the code does not do is worse than no tooltip.
//
// Note the Human definition admits its own limitation. That is deliberate:
// automation impersonating a browser lands there, it is true of every tool in
// this category, and saying so is what makes the other categories believable.
export const VISITOR_CATEGORY_TOOLTIPS: Record<string, string> = {
  human:
    "A visitor whose browser looks like an ordinary person's and didn't identify itself as automated. " +
    "Automation that deliberately impersonates a browser can land here too. That's true of every analytics tool.",
  ai_agent:
    "An AI assistant that fetched this page because a person asked it something. " +
    "There's a real prospect behind this visit.",
  ai_crawler:
    "An AI company collecting content to train its models. No person is waiting on the result.",
  search_engine_bot:
    "A search engine indexing your site so you can appear in results. Normal and desirable.",
  other_bot:
    "Automated software that identified itself but isn't a search engine or AI system, " +
    "SEO tools, uptime monitors, scrapers.",
  unidentified:
    "Didn't declare itself automated, but its browser signature doesn't match any real browser either. " +
    "Could be a privacy-hardened browser, an accessibility tool, or something hiding.",
  ai_bot:
    "AI traffic recorded before assistants and crawlers were separated (24 Aug 2026). " +
    "Which one it was can't be recovered.",
};

// Traffic Source values, kept in a SEPARATE map from VISITOR_CATEGORY_LABELS
// even though formatUnderscoredLabel consults both. The two describe
// genuinely different things and share no values: visitor_category is WHO is
// visiting, traffic_source is WHERE they came from. Merging them would invite
// a future collision between a category and a source that happen to share a
// string, and the override is silent when that happens.
//
// Only 'ai' needs an entry - the generic formatter already renders
// "organic_search" as "Organic Search" correctly, but it renders "ai" as
// "Ai", which looks careless on exactly the screen you would show a prospect.
//
// "AI Assistant" and NOT "AI": this is a HUMAN who asked ChatGPT something,
// got this site in the answer and clicked through. It is unrelated to the
// AI Agent / AI Crawler visitor categories, which are machines. Labelling
// both "AI" would conflate a customer with a bot.
const TRAFFIC_SOURCE_LABELS: Record<string, string> = {
  ai: "AI Assistant",
};

export const formatUnderscoredLabel = (value: string): string =>
  VISITOR_CATEGORY_LABELS[value] ??
  TRAFFIC_SOURCE_LABELS[value] ??
  value
    .split("_")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");

// Search-engine slugs as the backend stores them (lowercase; Common/Helper/trafficSource.ts), written the way each engine writes its own name. The generic
// formatter would give "Duckduckgo".
const SEARCH_ENGINE_LABELS: Record<string, string> = {
  google: "Google",
  yahoo: "Yahoo",
  bing: "Bing",
  baidu: "Baidu",
  ask: "Ask",
  aol: "AOL",
  excite: "Excite",
  duckduckgo: "DuckDuckGo",
  yandex: "Yandex",
  brave: "Brave Search",
  ecosia: "Ecosia",
  startpage: "Startpage",
  qwant: "Qwant",
  naver: "Naver",
  seznam: "Seznam",
  sogou: "Sogou",
  mojeek: "Mojeek",
  dogpile: "Dogpile",
  lycos: "Lycos",
  swisscows: "Swisscows",
};

// Filters whose stored values are raw keys never meant for display ('organic_search', 'ai', 'search_engine_bot', 'new', 'duckduckgo'). ONE rule for the sidebar and the
// tooltips, so the same value cannot read two ways. Every other filter's values are already names (a country, a browser) and are shown as they are.
// Social networks as the backend stores them (slugs), written the way each network writes its own name. "x" is X (formerly Twitter).
const SOCIAL_NETWORK_LABELS: Record<string, string> = {
  facebook: "Facebook",
  x: "X",
  linkedin: "LinkedIn",
  reddit: "Reddit",
  instagram: "Instagram",
  pinterest: "Pinterest",
  tiktok: "TikTok",
  threads: "Threads",
  snapchat: "Snapchat",
  youtube: "YouTube",
  rumble: "Rumble",
  substack: "Substack",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  discord: "Discord",
  tumblr: "Tumblr",
  mastodon: "Mastodon",
  bluesky: "Bluesky",
  quora: "Quora",
  medium: "Medium",
  twitch: "Twitch",
  vk: "VK",
  weibo: "Weibo",
  wechat: "WeChat",
  line: "LINE",
  truthsocial: "Truth Social",
  gab: "Gab",
  flickr: "Flickr",
  nextdoor: "Nextdoor",
};
const READABLE_LABEL_MENUS = new Set(["visitor_category", "visitor_type", "traffic_source", "search_engine", "social_network", "referrer"]);
export const needsReadableLabel = (menuType: string | undefined): boolean => !!menuType && READABLE_LABEL_MENUS.has(menuType);

// The bucket for sessions with no value for the active filter is stored as "(No data for this category)" (unknownBucket.ts - the backend compares against that exact
// string, so it stays the KEY). What is SHOWN says what the absence means for that filter, in a few words that fit the sidebar, and carries a hover explanation.
const NO_DATA_COPY: Record<string, { label: string; tip: string }> = {
  search_engine: { label: "Not from search", tip: "These visitors didn't arrive from a search engine: they typed the address, followed a link, or came from somewhere else." },
  social_network: { label: "Not from social", tip: "These visitors didn't arrive from a social network: they typed the address, came from a search, or followed another link." },
  country_name: { label: "Location not detected", tip: "The visitor's network didn't reveal where they are, which is common with VPNs, mobile carriers and company networks." },
  os: { label: "System not detected", tip: "The visitor's browser didn't say which operating system it runs on." },
  browser: { label: "Browser not detected", tip: "The visitor's browser didn't identify itself." },
  device: { label: "Device not detected", tip: "The visitor's browser didn't say what kind of device it is." },
  visitor_type: { label: "Not classified", tip: "Not enough was known about these visits to say whether the visitor is new or returning." },
  visitor_category: { label: "Not classified", tip: "Not enough was known about these visitors to say whether they are people or automated." },
  traffic_source: { label: "Not recorded", tip: "No referrer was recorded for these visits, so where they came from can't be told." },
  referrer: { label: "Not recorded", tip: "No referring page was recorded for these visits." },
};
const NO_DATA_FALLBACK = { label: "Not recorded", tip: "Nothing was recorded for these visitors under this filter." };
/** True for the stored no-value bucket and the literal Unknown some fields carry: both mean "nothing to say", and both read the same way. */
export const isNoDataValue = (value: unknown): boolean => typeof value === "string" && (value === NO_CATEGORY_DATA || /^unknown$/i.test(value));
export const noDataTooltip = (menuType: string | undefined): string => (NO_DATA_COPY[menuType ?? ""] ?? NO_DATA_FALLBACK).tip;

/** A link as people say it: no "https://", no "www.", and no lone trailing slash. "https://www.facebook.com/" -> "facebook.com"; a real path is kept ("medium.com/@a/post"). "/" (a visit with no referrer) is left for the callers that label it "Direct". */
export const cleanLink = (value: string): string => {
  if (typeof value !== "string" || value === "/") {
    return value;
  }
  const cleaned = value.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "");

  return cleaned || value;
};

export const readableLabel = (menuType: string | undefined, value: string): string => {
  if (isNoDataValue(value)) {
    return (NO_DATA_COPY[menuType ?? ""] ?? NO_DATA_FALLBACK).label;
  }
  if (!needsReadableLabel(menuType) || typeof value !== "string") {
    return value;
  }
  if (menuType === "referrer") {
    return cleanLink(value);
  }
  if (menuType === "search_engine") {
    return SEARCH_ENGINE_LABELS[value.toLowerCase()] ?? formatUnderscoredLabel(value);
  }
  if (menuType === "social_network") {
    return SOCIAL_NETWORK_LABELS[value.toLowerCase()] ?? formatUnderscoredLabel(value);
  }

  return formatUnderscoredLabel(value);
};

export const wrapPath =(originalPathName)=> {
  let isWrap = false;

  if (originalPathName.length > 29) {
    originalPathName = originalPathName.substr(0, 28);
    originalPathName = originalPathName + "...";
    isWrap = true;
  }

  return {
    pathName:originalPathName,
    isWrap
  };
};

/*
 * 🔴 "UNKNOWN" IS A SENTINEL, NOT A PLACE.
 *
 * LocationService (backend) writes the literal string 'UNKNOWN' into
 * region_name, city, country_name and time_zone whenever the geo lookup has
 * no data for that field. It is a real, truthy string, so every
 * `.filter(Boolean)` and every template literal in the UI happily renders it
 * - which is how a session tooltip came to read
 * "Region: UNKNOWN / City: UNKNOWN".
 *
 * That is not rare: measured against production 2026-09-23, 28.7% of sessions
 * have region UNKNOWN and 28.6% have city UNKNOWN, because geoip-lite's free
 * GeoLite2 data is country-only for datacenter, VPN, mobile-carrier and many
 * non-US/EU ranges. Country itself resolves for 99.9%.
 *
 * So the UI rule is: show a geo field only if it actually resolved. Telling
 * someone their visitor is in "UNKNOWN" is worse than telling them nothing -
 * it reads as a broken field rather than as an absent one, and DESIGN_RULES
 * says an empty state must never describe a refusal.
 */
export const GEO_UNKNOWN = "UNKNOWN";

export const isResolvedGeo = (value?: string | null): boolean =>
  Boolean(value) && value !== GEO_UNKNOWN;

// Joins the geo parts that actually resolved, e.g. "Austin, Texas", or just
// "Texas", or "" when neither did. Callers decide what to do with "".
export const joinResolvedGeo = (...parts: (string | null | undefined)[]): string =>
  parts.filter(isResolvedGeo).join(", ");
